/**
 * BuildingMapView
 * -----------------------------------------------------------------------------
 * Visual floor-plan-style view of housing units grouped by Building → Floor.
 * Each unit renders as a colour-coded tile so coordinators can see whole-building
 * occupancy at a glance.
 *
 *   🟢 Available      Any bed free
 *   🟡 Nearly Full    ≤25% beds free
 *   🔴 Full           All beds occupied
 *   ⚫ Out of Service  Maintenance / blocked
 *
 * Props:
 *   units             -> array of housing_units (event) OR accommodation_rooms
 *   delegates         -> array of delegates (each with housing_unit_id) — optional
 *   onUnitClick(unit) -> click handler on a tile
 *   getOccupancy(u)   -> optional override to compute {occupied, capacity} per unit
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, ChevronDown, ChevronRight } from 'lucide-react';

const stateColor = {
  available: 'bg-green-500 hover:bg-green-600',
  nearly_full: 'bg-yellow-500 hover:bg-yellow-600',
  full: 'bg-red-500 hover:bg-red-600',
  out_of_service: 'bg-gray-500 hover:bg-gray-600',
};
const stateRing = {
  available: 'ring-green-200',
  nearly_full: 'ring-yellow-200',
  full: 'ring-red-200',
  out_of_service: 'ring-gray-200',
};

function computeState(occupied, capacity, status) {
  if (status === 'maintenance' || status === 'blocked') return 'out_of_service';
  if (!capacity) return 'out_of_service';
  const available = capacity - occupied;
  if (available <= 0) return 'full';
  if (available / capacity <= 0.25) return 'nearly_full';
  return 'available';
}

const BuildingMapView = ({ units = [], delegates = [], onUnitClick, getOccupancy }) => {
  const [collapsedBuildings, setCollapsedBuildings] = useState({});

  // Enrich units with occupancy
  const enriched = useMemo(() => {
    const counts = {};
    for (const d of delegates) {
      if (d.housing_unit_id) counts[d.housing_unit_id] = (counts[d.housing_unit_id] || 0) + 1;
    }
    return units.map((u) => {
      let occupied, capacity;
      if (typeof getOccupancy === 'function') {
        const r = getOccupancy(u) || {};
        occupied = r.occupied ?? counts[u.id] ?? u.occupied ?? 0;
        capacity = r.capacity ?? Number(u.capacity) ?? 0;
      } else {
        occupied = u.occupied ?? counts[u.id] ?? 0;
        capacity = Number(u.capacity) || 0;
      }
      const state = computeState(occupied, capacity, u.status);
      return { ...u, __occupied: occupied, __capacity: capacity, __state: state };
    });
  }, [units, delegates, getOccupancy]);

  // Group Building → Floor → units
  const grouped = useMemo(() => {
    const g = {};
    for (const u of enriched) {
      const building = u.building || u.building_name || 'Unassigned Building';
      const floor = u.floor != null ? String(u.floor) : '—';
      if (!g[building]) g[building] = {};
      if (!g[building][floor]) g[building][floor] = [];
      g[building][floor].push(u);
    }
    // Sort each floor's units by name / number
    for (const b of Object.keys(g)) {
      for (const f of Object.keys(g[b])) {
        g[b][f].sort((a, b) => {
          const aNum = parseInt((a.name || a.room_number || '').match(/\d+/)?.[0] || '0', 10);
          const bNum = parseInt((b.name || b.room_number || '').match(/\d+/)?.[0] || '0', 10);
          if (aNum !== bNum) return aNum - bNum;
          return (a.name || a.room_number || '').localeCompare(b.name || b.room_number || '');
        });
      }
    }
    return g;
  }, [enriched]);

  // Building-level summary
  const buildingSummary = (building) => {
    const floors = grouped[building];
    let capacity = 0, occupied = 0, count = 0;
    for (const f of Object.keys(floors)) {
      for (const u of floors[f]) {
        capacity += u.__capacity;
        occupied += u.__occupied;
        count += 1;
      }
    }
    const rate = capacity ? Math.round((occupied / capacity) * 100) : 0;
    return { capacity, occupied, count, rate };
  };

  const toggle = (b) => setCollapsedBuildings((prev) => ({ ...prev, [b]: !prev[b] }));

  if (enriched.length === 0) {
    return (
      <Card className="p-12 text-center text-sm text-slate-500">
        <Building2 className="w-6 h-6 mx-auto mb-2 text-slate-300" />
        No rooms to display. Add a building and rooms first.
      </Card>
    );
  }

  const buildings = Object.keys(grouped).sort();

  return (
    <div className="space-y-3" data-testid="building-map-view">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600 bg-slate-50 border rounded-md px-3 py-2">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-green-500" />Available</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-yellow-500" />Nearly Full</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500" />Full</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-500" />Out of Service</span>
        <span className="ml-auto text-slate-400">Click any room to open its details</span>
      </div>

      {buildings.map((building) => {
        const summary = buildingSummary(building);
        const collapsed = collapsedBuildings[building];
        const floors = Object.keys(grouped[building]).sort((a, b) => {
          const an = parseInt(a, 10); const bn = parseInt(b, 10);
          if (!isNaN(an) && !isNaN(bn)) return an - bn;
          return a.localeCompare(b);
        });
        return (
          <Card key={building} className="overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(building)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-b text-left"
              data-testid={`map-building-toggle-${building.replace(/\s+/g, '-').toLowerCase()}`}
            >
              {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              <Building2 className="w-4 h-4 text-indigo-500" />
              <span className="font-semibold">{building}</span>
              <span className="text-xs text-slate-500">
                {summary.count} room{summary.count === 1 ? '' : 's'} · {summary.occupied}/{summary.capacity} beds
              </span>
              <Badge variant="outline" className="ml-auto text-xs">
                {summary.rate}% occupied
              </Badge>
            </button>

            {!collapsed && (
              <CardContent className="p-4 space-y-4">
                {floors.map((floor) => {
                  const units = grouped[building][floor];
                  return (
                    <div key={floor}>
                      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
                        <span className="font-medium">Floor {floor}</span>
                        <span className="h-px flex-1 bg-slate-100" />
                        <span>{units.length} room{units.length === 1 ? '' : 's'}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {units.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => onUnitClick?.(u)}
                            className={`text-left rounded-md text-white p-2 text-xs ${stateColor[u.__state]} ring-2 ring-transparent hover:${stateRing[u.__state]} transition-all`}
                            data-testid={`map-unit-${u.id}`}
                            title={`${u.name || u.room_number} · ${u.__occupied}/${u.__capacity} beds`}
                          >
                            <div className="font-semibold truncate">{u.name || u.room_number || u.id.slice(-4)}</div>
                            <div className="opacity-90 flex items-center gap-1 mt-0.5">
                              <Users className="w-3 h-3" />
                              <span>{u.__occupied}/{u.__capacity}</span>
                            </div>
                            {u.__state === 'out_of_service' && (
                              <div className="opacity-90 text-[10px] mt-0.5 capitalize">{u.status || 'N/A'}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default BuildingMapView;
